import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { get } from '../../services/apiService';

const SEVERITY_COLORS = {
  Critical: '#d32f2f',
  High:     '#f57c00',
  Medium:   '#f9a825',
  Low:      '#388e3c'
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, e) => s + (e.value || 0), 0);
  return (
    <Paper sx={{ p: 1.5, border: '1px solid #ccc', minWidth: 160 }}>
      <Typography variant="body2" fontWeight="bold" mb={0.5}>Week of {label}</Typography>
      {payload.map((entry, i) => (
        <Box key={i} display="flex" justifyContent="space-between" gap={2}>
          <Typography variant="caption" color={entry.color}>{entry.name}</Typography>
          <Typography variant="caption" fontWeight="bold">{entry.value}</Typography>
        </Box>
      ))}
      <Box display="flex" justifyContent="space-between" gap={2} mt={0.5} borderTop="1px solid #eee" pt={0.5}>
        <Typography variant="caption" fontWeight="bold">Total</Typography>
        <Typography variant="caption" fontWeight="bold">{total}</Typography>
      </Box>
    </Paper>
  );
};

const SnykWeeklySlaChart = ({ showUnresolved = true, outOfSLA = false, collection = '' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const loadedRef = useRef(false);
  const prevPropsRef = useRef({ showUnresolved, outOfSLA, collection });

  useEffect(() => {
    const propsChanged =
      prevPropsRef.current.showUnresolved !== showUnresolved ||
      prevPropsRef.current.outOfSLA !== outOfSLA ||
      prevPropsRef.current.collection !== collection;

    prevPropsRef.current = { showUnresolved, outOfSLA, collection };

    if (expanded && (propsChanged || !loadedRef.current)) {
      fetchData();
    }
  }, [showUnresolved, outOfSLA, collection, expanded]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (outOfSLA) params.append('outOfSLA', 'true');
      else if (showUnresolved) params.append('showUnresolved', 'true');
      if (collection) params.append('projectCollection', collection);

      const response = await get(`/api/snyk/weekly-sla?${params}`);
      if (response?.success) {
        setData(response.data || []);
        loadedRef.current = true;
      } else {
        throw new Error('Invalid response');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch weekly SLA data');
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (_, isExpanded) => {
    setExpanded(isExpanded);
  };

  // Current week label for reference line
  const todayLabel = (() => {
    const d = new Date();
    const dow = d.getDay() || 7;
    const mon = new Date(d);
    mon.setDate(d.getDate() - dow + 1);
    return mon.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  })();

  const totalIssues = data.reduce((s, w) => s + w.total, 0);
  const overdueWeeks = data.filter(w => w.weekStart < new Date().toISOString().split('T')[0]).length;

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="h6">Weekly SLA Due Dates</Typography>
            <Typography variant="body2" color="text.secondary">
              Open issues grouped by SLA deadline week
              {loadedRef.current && (
                <>
                  {' · '}{totalIssues.toLocaleString()} issues across {data.length} weeks
                  {overdueWeeks > 0 && (
                    <Chip label={`${overdueWeeks} overdue week${overdueWeeks > 1 ? 's' : ''}`} color="error" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                  )}
                </>
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          )}
          {!loading && error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && data.length === 0 && (
            <Typography variant="body2" color="text.secondary">No SLA data available.</Typography>
          )}
          {!loading && !error && data.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Each bar represents open issues whose SLA due date falls within that week. Past weeks indicate overdue issues.
              </Typography>
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" />
                  <ReferenceLine x={todayLabel} stroke="#1976d2" strokeDasharray="4 2" label={{ value: 'Today', position: 'top', fontSize: 11, fill: '#1976d2' }} />
                  {['Critical', 'High', 'Medium', 'Low'].map(sev => (
                    <Bar key={sev} dataKey={sev} stackId="sla" fill={SEVERITY_COLORS[sev]} maxBarSize={40} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SnykWeeklySlaChart;
